import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Pencil, Trash2, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  STATI_COMMESSA, statoCommessaColor, deadlineStyle, deadlineLabel, giorniRimanenti, fmtData,
} from "../lib/helpers";

const TIPI_RAPPORTO = {
  dipendente: "Dipendente",
  collaboratore: "Collaboratore",
  socio: "Socio",
  libero_professionista: "Libero professionista",
};

const EMPTY_FORM = {
  nome: "", cognome: "", ruolo: "", tipo_rapporto: "dipendente",
  email: "", telefono: "", data_inizio: "", competenze: "", attivo: true,
};

function DipendenteForm({ open, onClose, onSaved, dipendente }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(dipendente ? { ...EMPTY_FORM, ...dipendente } : EMPTY_FORM);
  }, [open, dipendente]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, email: form.email || null, telefono: form.telefono || null, data_inizio: form.data_inizio || null };
      if (dipendente) await api.put(`/dipendenti/${dipendente.id}`, payload);
      else await api.post("/dipendenti", payload);
      toast.success(dipendente ? "Dipendente aggiornato" : "Dipendente aggiunto");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900/30";
  const labelCls = "block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="dipendente-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{dipendente ? "Modifica anagrafica" : "Nuovo dipendente / collaboratore"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Nome *</label>
            <input data-testid="dipendente-form-nome" required value={form.nome} onChange={(e) => set("nome", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Cognome *</label>
            <input data-testid="dipendente-form-cognome" required value={form.cognome} onChange={(e) => set("cognome", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Ruolo / qualifica</label>
            <input data-testid="dipendente-form-ruolo" value={form.ruolo} onChange={(e) => set("ruolo", e.target.value)} className={inputCls} placeholder="es. Ingegnere strutturista" />
          </div>
          <div>
            <label className={labelCls}>Tipo di rapporto</label>
            <Select value={form.tipo_rapporto} onValueChange={(v) => set("tipo_rapporto", v)}>
              <SelectTrigger data-testid="dipendente-form-rapporto"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TIPI_RAPPORTO).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input data-testid="dipendente-form-email" type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Telefono</label>
            <input data-testid="dipendente-form-telefono" value={form.telefono || ""} onChange={(e) => set("telefono", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Data inizio rapporto</label>
            <input data-testid="dipendente-form-data-inizio" type="date" value={form.data_inizio || ""} onChange={(e) => set("data_inizio", e.target.value)} className={inputCls} />
          </div>
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" data-testid="dipendente-form-attivo" checked={form.attivo} onChange={(e) => set("attivo", e.target.checked)} className="h-4 w-4 accent-blue-900" />
              Attivo
            </label>
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Competenze / specializzazioni</label>
            <textarea data-testid="dipendente-form-competenze" rows={2} value={form.competenze} onChange={(e) => set("competenze", e.target.value)} className={inputCls} placeholder="es. Strutture in c.a., verifiche sismiche, computi metrici" />
          </div>
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} data-testid="dipendente-form-cancel" className="px-4 py-2 text-sm font-medium border border-slate-300 rounded-md hover:bg-slate-50">Annulla</button>
            <button type="submit" disabled={saving} data-testid="dipendente-form-submit" className="px-4 py-2 text-sm font-semibold bg-blue-900 text-white rounded-md hover:bg-blue-950 disabled:opacity-60">
              {saving ? "Salvataggio…" : "Salva"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CaricoPanel({ dipendente, onClose }) {
  const [carico, setCarico] = useState(null);

  useEffect(() => {
    setCarico(null);
    api.get(`/dipendenti/${dipendente.id}/carico`).then((r) => setCarico(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [dipendente.id]);

  const scadenze = carico
    ? [...carico.commesse.map((c) => ({ ...c, _tipo: "Commessa", _data: c.scadenza })),
       ...carico.gare.map((g) => ({ ...g, _tipo: "Gara", _data: g.scadenza_offerta }))]
        .filter((x) => x._data)
        .sort((a, b) => a._data.localeCompare(b._data))
    : [];

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="carico-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Carico di lavoro — {dipendente.nome} {dipendente.cognome}</DialogTitle>
        </DialogHeader>
        {!carico ? (
          <div className="py-8 text-center text-sm text-slate-500">Caricamento…</div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                <div className="font-display text-2xl font-bold text-blue-900">{carico.commesse.length}</div>
                <div className="text-xs text-slate-600">Commesse attive</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <div className="font-display text-2xl font-bold text-amber-800">{carico.gare.length}</div>
                <div className="text-xs text-slate-600">Gare assegnate</div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                <div className="font-display text-2xl font-bold text-slate-700">{carico.storico.length}</div>
                <div className="text-xs text-slate-600">Commesse chiuse</div>
              </div>
            </div>

            <section>
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Calendario personale scadenze</h3>
              {scadenze.length === 0 ? (
                <div className="text-sm text-slate-500">Nessuna scadenza assegnata.</div>
              ) : (
                <div className="space-y-1.5">
                  {scadenze.map((x) => {
                    const gg = giorniRimanenti(x._data);
                    const st = deadlineStyle(gg);
                    return (
                      <div key={x._tipo + x.id} data-testid={`carico-scadenza-${x.id}`} className={`flex items-center justify-between text-sm border border-slate-200 rounded-md px-3 py-2 ${st.row}`}>
                        <div className="min-w-0">
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mr-2">{x._tipo}</span>
                          <span className="text-slate-800">{x._tipo === "Commessa" ? `${x.codice} — ` : ""}{x.denominazione}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-slate-500 font-code">{fmtData(x._data)}</span>
                          {gg !== null && <Badge variant="outline" className={`${st.badge} font-code`}>{deadlineLabel(gg)}</Badge>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            <section>
              <h3 className="text-sm font-semibold text-slate-800 mb-2">Commesse assegnate</h3>
              {carico.commesse.length === 0 ? (
                <div className="text-sm text-slate-500">Nessuna commessa attiva.</div>
              ) : (
                <div className="space-y-1.5">
                  {carico.commesse.map((c) => (
                    <div key={c.id} className="flex items-center justify-between text-sm border border-slate-200 rounded-md px-3 py-2">
                      <div className="min-w-0">
                        <span className="font-code text-xs text-slate-500 mr-2">{c.codice}</span>
                        <span className="text-slate-800">{c.denominazione}</span>
                        {c.responsabile_id === dipendente.id && <span className="ml-2 text-[10px] font-semibold text-blue-900">RESPONSABILE</span>}
                      </div>
                      <Badge variant="outline" className={`${statoCommessaColor[c.stato]} shrink-0`}>{STATI_COMMESSA[c.stato]}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {carico.storico.length > 0 && (
              <section>
                <h3 className="text-sm font-semibold text-slate-800 mb-2">Storico commesse chiuse</h3>
                <div className="space-y-1.5">
                  {carico.storico.map((c) => (
                    <div key={c.id} className="flex items-center justify-between text-sm border border-slate-100 bg-slate-50 rounded-md px-3 py-2">
                      <span className="text-slate-600"><span className="font-code text-xs mr-2">{c.codice}</span>{c.denominazione}</span>
                      <span className="text-xs text-slate-400">{c.committente}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Dipendenti() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [dipendenti, setDipendenti] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [caricoDi, setCaricoDi] = useState(null);
  const [searchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, dash] = await Promise.all([api.get("/dipendenti"), api.get("/dashboard")]);
      setDipendenti(d.data);
      setDashboard(dash.data);
      const sel = searchParams.get("sel");
      if (sel) {
        const found = d.data.find((x) => x.id === sel);
        if (found) setCaricoDi(found);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => { load(); }, [load]);

  const elimina = async (d) => {
    if (!window.confirm(`Eliminare ${d.nome} ${d.cognome}?`)) return;
    try {
      await api.delete(`/dipendenti/${d.id}`);
      toast.success("Dipendente eliminato");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const caricoMap = {};
  (dashboard?.carico || []).forEach((c) => { caricoMap[c.dipendente.id] = c; });

  return (
    <div className="space-y-5" data-testid="dipendenti-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">Dipendenti & collaboratori</h1>
          <p className="text-sm text-slate-500 mt-1">{dipendenti.filter((d) => d.attivo).length} attivi · clicca su una riga per il carico di lavoro</p>
        </div>
        {isAdmin && (
          <button data-testid="dipendenti-add-trigger" onClick={() => { setEditing(null); setDialogOpen(true); }}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md bg-blue-900 text-white hover:bg-blue-950">
            <Plus className="h-4 w-4" /> Nuovo dipendente
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-500 text-sm" data-testid="dipendenti-loading">Caricamento anagrafica…</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="dipendenti-workload-chart">
          <table className="w-full text-sm" data-testid="dipendenti-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left">
                {["Nome", "Ruolo", "Rapporto", "Contatti", "Inizio", "Competenze", "Carico attuale", ...(isAdmin ? [""] : [])].map((h, i) => (
                  <th key={i} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dipendenti.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">Nessun dipendente in anagrafica.</td></tr>
              )}
              {dipendenti.map((d) => {
                const c = caricoMap[d.id];
                const totale = c ? c.totale : 0;
                return (
                  <tr key={d.id} data-testid={`dipendente-row-${d.id}`} onClick={() => setCaricoDi(d)}
                    className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/70 cursor-pointer ${!d.attivo ? "opacity-50" : ""}`}>
                    <td className="px-3 py-2.5">
                      <div className="font-medium text-slate-800">{d.nome} {d.cognome}</div>
                      {!d.attivo && <span className="text-[10px] text-slate-400 uppercase">non attivo</span>}
                    </td>
                    <td className="px-3 py-2.5 text-slate-600">{d.ruolo || "—"}</td>
                    <td className="px-3 py-2.5">
                      <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-200">{TIPI_RAPPORTO[d.tipo_rapporto] || d.tipo_rapporto}</Badge>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-600">
                      {d.email && <div className="flex items-center gap-1"><Mail className="h-3 w-3 text-slate-400" />{d.email}</div>}
                      {d.telefono && <div className="flex items-center gap-1"><Phone className="h-3 w-3 text-slate-400" />{d.telefono}</div>}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-slate-600 font-code">{fmtData(d.data_inizio)}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600 max-w-56 truncate" title={d.competenze}>{d.competenze || "—"}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">
                      <Badge variant="outline" className={totale >= 7 ? "bg-red-100 text-red-800 border-red-200 font-code" : totale >= 4 ? "bg-amber-100 text-amber-800 border-amber-200 font-code" : "bg-green-100 text-green-800 border-green-200 font-code"}>
                        {totale >= 7 ? "Sovraccarico" : totale >= 4 ? "Impegnato" : "Disponibile"} ({totale})
                      </Badge>
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-2.5 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                        <button data-testid={`dipendente-edit-${d.id}`} onClick={() => { setEditing(d); setDialogOpen(true); }} className="p-1.5 rounded hover:bg-slate-200 text-slate-500"><Pencil className="h-3.5 w-3.5" /></button>
                        <button data-testid={`dipendente-delete-${d.id}`} onClick={() => elimina(d)} className="p-1.5 rounded hover:bg-red-100 text-slate-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <DipendenteForm open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={load} dipendente={editing} />
      {caricoDi && <CaricoPanel dipendente={caricoDi} onClose={() => setCaricoDi(null)} />}
    </div>
  );
}
